// owner-map: OP-05c.spa
/**
 * Pure logic for turning a forecast's append-only `report_pointers` history into a
 * rendered report.
 *
 * A pointer is a small cache entry (`{kind, source, forecaster, row_key,
 * fire_ts, at}` for forecasts; `{kind, status, qkey, attempted_at, at}` for
 * resolutions) that the server appends on forecast/resolution completion. The
 * full report itself is NOT stored on the row — it lives in BigTable / BigQuery
 * and is fetched lazily via the `/api/forecast/report` / `/api/resolve/report`
 * proxies using the pointer's `row_key` / `qkey`.
 */

import { parseResolveDateLocal } from "../formatDates.js";
import { isConclusiveResolutionStatus } from "../report/resolutionBody.js";
import { SHIFT_DISPLAY_KINDS } from "./shiftStability.js";
import { extractMultiway, isNamedOutcomeVerdict } from "./multiway.js";
import { extractAlternateForecast } from "./alternateSpaces.js";
import { runAuthoredNoForecast } from "./unauthoredRun.js";
import { withLiveIdentity } from "./vintageContract.js";
import { withBackground } from "./contractBackground.js";

// A date-only "YYYY-MM-DD" is LOCAL midnight (not Date.parse's UTC midnight), the UI-wide
// convention that keeps the scrub list, the dedup, and the timeline marker from shifting a
// day in the user's timezone; a full ISO is its exact instant. An unparseable value yields
// Infinity, a sentinel `resolutionPointers` detects with `Number.isFinite` to drop the pointer.
function _resolutionMs(s) {
  const d = parseResolveDateLocal(s);
  const t = d ? d.getTime() : NaN;
  return Number.isNaN(t) ? Infinity : t;
}

function _recencyKey(pointer) {
  const fireMs = parseFireTs(pointer.fire_ts);
  const parsedAt = typeof pointer.at === "string" ? Date.parse(pointer.at) : NaN;
  const atMs = Number.isFinite(parsedAt) ? parsedAt : Number.NEGATIVE_INFINITY;
  return [Number.isFinite(fireMs) ? fireMs : atMs, atMs];
}

function _latestOfKind(pointers, kind) {
  if (!Array.isArray(pointers)) return null;
  let best = null;
  let bestKey = null;
  for (const p of pointers) {
    if (!p || p.kind !== kind) continue;
    const key = _recencyKey(p);
    if (best === null) {
      best = p;
      bestKey = key;
      continue;
    }
    // One key per record keeps comparison transitive across new pointers and
    // legacy/malformed records: valid fire_ts is authoritative; otherwise that
    // record falls back to append time. Append time breaks equal-fire ties, and
    // >= preserves the established later-array-entry winner for exact ties.
    if (key[0] > bestKey[0] || (key[0] === bestKey[0] && key[1] >= bestKey[1])) {
      best = p;
      bestKey = key;
    }
  }
  return best;
}

export function latestForecastPointer(pointers) {
  return _latestOfKind(pointers, "forecast");
}

/**
 * The latest `kind:'forecast'` pointer to use as the LIVE report overlay, or null when it is
 * STALE (its `fire_ts` predates `liveMomentIso`).
 *
 * A manual ↻ reforecast advances the live contract but writes NO forecast pointer, so the
 * latest pointer would duplicate the newest async scrub stop's report under a different
 * probability ("different probability, identical report"). Gating the overlay on
 * pointer-not-older-than-live keeps "current" on its own (server_ctx) render.
 *
 * Fail-open: when `liveMomentIso` or the pointer's `fire_ts` is unparseable, staleness can't
 * be proven, so the latest pointer is returned.
 *
 * @param {Array} pointers active.reportPointers
 * @param {string|null|undefined} liveMomentIso
 * @returns {object|null}
 */
export function selectLiveForecastPointer(pointers, liveMomentIso) {
  // A pointer with no row key names no report, so it can never be the live one — for the
  // overlay or a derive (the server skips it the same way).
  const readable = Array.isArray(pointers)
    ? pointers.filter((p) => typeof p?.row_key === "string" && p.row_key.length > 0)
    : [];
  const latest = latestForecastPointer(readable);
  if (!latest) return null;
  const liveMs = parseFireTs(liveMomentIso);
  const fireMs = parseFireTs(latest.fire_ts);
  if (Number.isNaN(liveMs) || Number.isNaN(fireMs)) return latest;
  // Strictly older than the live contract → stale — decided at WHOLE seconds, the precision a
  // compact fire_ts carries and the one the server uses (server/handlers/deriveBranch.js
  // liveForecastPointer). A row's own on-demand run is stamped within the second the row is
  // created, so any finer comparison would call that pointer stale here and live on the server.
  return Math.floor(fireMs / 1000) < Math.floor(liveMs / 1000) ? null : latest;
}

/**
 * This is the definition the server re-checks a derived branch against
 * (server/handlers/deriveBranch.js liveForecastPointer), pinned by the shared vectors in
 * `__fixtures__/livePointerVectors.json`. `lastReforecastedAt` is deliberately NOT an
 * input: the row has no such column, so a client that let it decide staleness could call
 * a pointer stale that the server calls live — a 409 the user cannot fix without a reload.
 *
 * @param {{createdAt?: string|null, history?: unknown[]}|null|undefined} run
 * @returns {string|null}
 */
export function liveContractMomentOf(run) {
  let latestMs = NaN;
  const consider = (iso) => {
    if (typeof iso !== "string" || !iso) return;
    const ms = Date.parse(iso);
    if (!Number.isNaN(ms) && (Number.isNaN(latestMs) || ms > latestMs)) latestMs = ms;
  };
  consider(run?.createdAt);
  for (const entry of Array.isArray(run?.history) ? run.history : []) {
    if (!entry || typeof entry !== "object") continue;
    consider(entry.snapshotAt ?? entry.generatedAt);
  }
  // Canonical, not verbatim: selectLiveForecastPointer re-reads it with the strict parseFireTs.
  return Number.isNaN(latestMs) ? null : new Date(latestMs).toISOString();
}

/**
 * The single client definition of the run's live forecast pointer, or null when the latest
 * pointer is stale. See liveContractMomentOf for why its inputs are what they are.
 */
export function liveForecastPointerOf(run) {
  return selectLiveForecastPointer(run?.reportPointers ?? [], liveContractMomentOf(run));
}

/**
 * Parse a report-pointer `fire_ts` to epoch ms. The compact form is the UTC `{ts}` segment
 * of an ondemand run_key `{tag}#{ts}`. Returns NaN when neither ISO nor compact form
 * matches, so callers drop the point rather than plot or scrub a bogus value.
 */
export function parseFireTs(fireTs) {
  if (typeof fireTs !== "string" || fireTs.length === 0) return NaN;
  const m = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/.exec(fireTs);
  if (m) {
    const [Y, Mo, D, h, mi, s] = m.slice(1).map(Number);
    const ms = Date.UTC(Y, Mo - 1, D, h, mi, s);
    // Date.UTC rolls out-of-range parts (month 13, day 32, hour 25, …) into a
    // DIFFERENT valid instant; reject those via a round-trip so a malformed
    // fire_ts is DROPPED (NaN), not silently shifted to the wrong moment.
    const d = new Date(ms);
    const ok =
      d.getUTCFullYear() === Y &&
      d.getUTCMonth() === Mo - 1 &&
      d.getUTCDate() === D &&
      d.getUTCHours() === h &&
      d.getUTCMinutes() === mi &&
      d.getUTCSeconds() === s;
    return ok ? ms : NaN;
  }
  const parts =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/.exec(
      fireTs,
    );
  if (!parts) return NaN;
  const [year, month, day, hour, minute, second] = parts.slice(1, 7).map(Number);
  const calendar = new Date(0);
  calendar.setUTCFullYear(year, month - 1, day);
  calendar.setUTCHours(hour, minute, second, 0);
  if (
    calendar.getUTCFullYear() !== year ||
    calendar.getUTCMonth() !== month - 1 ||
    calendar.getUTCDate() !== day ||
    calendar.getUTCHours() !== hour ||
    calendar.getUTCMinutes() !== minute ||
    calendar.getUTCSeconds() !== second ||
    (parts[7] !== undefined && (Number(parts[7]) > 23 || Number(parts[8]) > 59))
  )
    return NaN;
  const iso = Date.parse(fireTs);
  return Number.isNaN(iso) ? NaN : iso;
}

/**
 * The downstream trajectory pipeline assumes ISO everywhere, so only the compact form is
 * rewritten. A valid ISO is returned byte-identical, because the live-sentinel moment must
 * round-trip unchanged (see buildReforecastEntries).
 */
function _normalizeFireTs(fireTs) {
  const ms = parseFireTs(fireTs);
  if (Number.isNaN(ms)) return null;
  return fireTs.includes("_") ? new Date(ms).toISOString() : fireTs;
}

/**
 * The `kind:'forecast'` pointers of a report_pointers array (order preserved), with each
 * `fire_ts` normalized to ISO. A non-empty string `row_key` is required.
 *
 * Every forecast pointer is included regardless of `source`. A client-side manual `history`
 * line is session-only and never coexists with a fire's durable pointer, so there is no
 * double-count. ISO normalization happens here, the single choke point, so every consumer
 * downstream stays ISO-only.
 */
export function forecastPointers(pointers) {
  if (!Array.isArray(pointers)) return [];
  return pointers
    .filter(
      (p) => p && p.kind === "forecast" && typeof p.row_key === "string" && p.row_key.length > 0,
    )
    .flatMap((p) => {
      const fire_ts = _normalizeFireTs(p.fire_ts);
      if (fire_ts === null) return [];
      return [fire_ts === p.fire_ts ? p : { ...p, fire_ts }];
    });
}

/**
 * The `kind:'resolution'` pointers of a report_pointers array. Each is one resolution
 * ATTEMPT (appendResolutionPointer dedups on `(qkey, attempted_at)`), so a question can
 * carry several.
 *
 * A pointer whose `attempted_at` is missing or unparseable can't be placed on the timeline,
 * so it is dropped here rather than discarded downstream; a caller counting the result never
 * sees a "settled" attempt that can't render. Resolution pointers carry `status` inline, so
 * the scrub marker needs no fetch.
 *
 * Non-terminal `in_flight` attempts are excluded: the UI treats them as "still resolving",
 * not a settled outcome, so they must not become selectable scrub stops.
 *
 * @param {Array} pointers active.reportPointers
 * @returns {Array}
 */
export function resolutionPointers(pointers) {
  if (!Array.isArray(pointers)) return [];
  return pointers
    .filter(
      (p) =>
        p &&
        p.kind === "resolution" &&
        typeof p.attempted_at === "string" &&
        p.attempted_at.length > 0 &&
        p.status !== "in_flight",
    )
    .map((p) => ({ p, ms: _resolutionMs(p.attempted_at) }))
    .filter((x) => Number.isFinite(x.ms))
    .sort((a, b) => a.ms - b.ms)
    .map((x) => x.p);
}

/**
 * @param {object|null} pointer a kind:'forecast' report pointer
 * @param {object|null} source result of reportPayloadToSource (or null on miss)
 * @returns {{fireTs:string, p:number}|null}
 */
export function pointerToMoment(pointer, source) {
  if (!pointer || !source) return null;
  const p = source.serverPrediction;
  const fireTs = _normalizeFireTs(pointer.fire_ts);
  if (typeof p !== "number" || !Number.isFinite(p)) return null;
  if (fireTs === null) return null;
  return { fireTs, p };
}

/**
 * The moment of a fire that carries only a multi-value distribution: `p` is null.
 *
 * @param {object|null} pointer a kind:'forecast' report pointer
 * @param {object|null} dist the fire's `extractMultiway` distribution
 * @returns {{fireTs:string, p:null}|null}
 */
function distOnlyMoment(pointer, dist) {
  if (!pointer || !Array.isArray(dist?.buckets) || dist.buckets.length === 0) return null;
  const fireTs = _normalizeFireTs(pointer.fire_ts);
  return fireTs === null ? null : { fireTs, p: null };
}

/**
 * Events are in RIGHT convention: a forecast of `p` was made at `atMs` and holds until the
 * next event. Manual history stores the PRE-reforecast contract at each `snapshotAt`
 * (useReforecast snapshots the OLD contract), so the value produced at a moment is the NEXT
 * snapshot's probability, and the latest manual reforecast produced the live
 * `currentProbability`. Async pointers already store the value produced AT the fire.
 *
 * Seeding the generation point with `currentProbability` (the LATEST/drifted value) while
 * async fires exist would back-date today's probability onto it, making the trace start in
 * the wrong YES/NO zone.
 *
 * @param {Array} history active.history (manual ↻ snapshots); may be undefined
 * @param {Array<{fireTs:string,p:number}>} asyncMoments
 * @param {number} currentProbability
 * @param {string|number|Date} createdAt
 * @returns {Array<{atMs:number, p:number}>}
 */
export function buildReforecastMoments(history, asyncMoments, currentProbability, createdAt) {
  const _fin = (p) => typeof p === "number" && Number.isFinite(p);
  return buildValueMoments({
    history,
    asyncItems: asyncMoments,
    currentValue: currentProbability,
    createdAt,
    isValid: _fin,
    manualValueOf: (snap) => snap?.contract?.probability,
    asyncValueOf: (m) => m.p,
  }).map(({ atMs, value }) => ({ atMs, p: value }));
}

/**
 * The time convention behind every reforecast series, over an OPAQUE value.
 *
 * `buildReforecastMoments` (scalar probability) and the per-outcome series in `binSeries.js`
 * (a whole bucket distribution) plot the same vintages on the same timeline, so they must
 * agree on when each vintage's value was produced. That convention is subtle enough (manual
 * history is stored one step behind; async fires are not) that two copies would drift and
 * silently mis-date one line relative to the other, so it lives here once.
 *
 * @param {{
 *   history?: Array,
 *   asyncItems?: Array,
 *   currentValue: any,
 *   createdAt?: string|number|Date,
 *   isValid: (v: any) => boolean,
 *   manualValueOf: (snap: any) => any,
 *   asyncValueOf: (item: any) => any,
 * }} args
 * @returns {Array<{atMs:number, value:any}>}
 */
export function buildValueMoments({
  history,
  asyncItems,
  currentValue,
  createdAt,
  isValid,
  manualValueOf,
  asyncValueOf,
}) {
  const manual = Array.isArray(history) ? history : [];
  const asyncArr = Array.isArray(asyncItems) ? asyncItems : [];
  const events = [];
  const createdMs = createdAt != null ? new Date(createdAt).getTime() : NaN;
  let earliestAsyncValue = null;
  let earliestAsyncMs = Infinity;
  for (const m of asyncArr) {
    if (!m || !m.fireTs) continue;
    const v = asyncValueOf(m);
    if (!isValid(v)) continue;
    const t = parseFireTs(m.fireTs);
    if (Number.isNaN(t) || t >= earliestAsyncMs) continue;
    earliestAsyncMs = t;
    earliestAsyncValue = v;
  }
  // Generation: value held from createdAt until the first reforecast. With manual ↻
  // history, history[0] is the pre-first-reforecast (original) contract. With no manual
  // history but async fires present, the earliest async fire carries the generation-era
  // value; using it (not the latest/drifted currentValue) keeps the line's first point at
  // the original forecast.
  if (!Number.isNaN(createdMs)) {
    const v0 =
      manual.length > 0
        ? manualValueOf(manual[0])
        : earliestAsyncValue != null
          ? earliestAsyncValue
          : currentValue;
    if (isValid(v0)) events.push({ atMs: createdMs, value: v0 });
  }
  // Each manual reforecast produced the value stored in the NEXT snapshot (or the live
  // value for the latest one).
  for (let i = 0; i < manual.length; i++) {
    const t = new Date(manual[i].snapshotAt || manual[i].generatedAt || NaN).getTime();
    const v = i + 1 < manual.length ? manualValueOf(manual[i + 1]) : currentValue;
    if (!Number.isNaN(t) && isValid(v)) events.push({ atMs: t, value: v });
  }
  for (const m of asyncArr) {
    if (!m || !m.fireTs) continue;
    const v = asyncValueOf(m);
    if (!isValid(v)) continue;
    const t = parseFireTs(m.fireTs);
    if (!Number.isNaN(t)) events.push({ atMs: t, value: v });
  }
  events.sort((a, b) => a.atMs - b.atMs);
  const out = [];
  let lastMs = null;
  for (const e of events) {
    if (e.atMs === lastMs) continue;
    out.push(e);
    lastMs = e.atMs;
  }
  return out;
}

/**
 * Best-effort: a pointer whose report is missing / errors, or that carries neither a numeric
 * prediction nor a multi-value distribution, is omitted. A multi-value fire comes back with
 * `p: null` and its `dist`, so check `p` is finite before using it. Order follows input
 * order; `buildReforecastMoments` time-sorts.
 *
 * @param {Array} forecastPtrs kind:'forecast' pointers (see `forecastPointers`)
 * @param {{ apiGet: Function, apiPost?: Function, cache: object }} deps
 * @returns {Promise<Array<{fireTs:string, p:number|null, dist:object|null}>>}
 */
export async function fetchForecastMoments(forecastPtrs, { apiGet, apiPost, cache }) {
  return (await fetchForecastSeries(forecastPtrs, { apiGet, apiPost, cache })).moments;
}

/**
 * One fetch yields both the plot `moments` and the per-fire shift `verdicts`: the FORECAST
 * STABILITY aggregate (see `lib/forecast/shiftStability.js`) needs a verdict per fire across
 * the same N rows the plot line already reads, and fetching them twice would double the
 * read cost.
 *
 * `verdicts` includes an entry for EVERY fire whose report resolved (even one carrying no
 * verdict, as `{shift: null, display: null}`), unlike `moments`, which drops a fire with
 * neither a finite prediction nor a multi-value distribution (a multi-value fire's moment
 * has `p: null`). That lets the aggregate distinguish "no fire had a verdict" from
 * "no fire loaded", which is its empty state.
 *
 * @param {Array} forecastPtrs kind:'forecast' report pointers
 * @param {{ apiGet: Function, apiPost?: Function, cache: object }} deps
 * @returns {Promise<{moments: Array<{fireTs:string,p:number|null,dist:object|null}>, verdicts: Array<object>}>}
 */
export async function fetchForecastSeries(forecastPtrs, { apiGet, apiPost, cache }) {
  // Skip null/malformed entries so one bad pointer can't throw synchronously in .map and
  // break the whole series fetch.
  const list = (Array.isArray(forecastPtrs) ? forecastPtrs : []).filter(
    (p) => p && typeof p.row_key === "string" && p.row_key.length > 0,
  );
  if (list.length === 0) return { moments: [], verdicts: [] };
  // ONE batched round-trip primes the cache for all uncached keys, so the per-key map
  // below hits the cache instead of issuing N single reads. Keys the batch could not
  // prime fall back to a per-key fetch.
  if (apiPost)
    await primeForecastReportsCache(
      list.map((p) => p.row_key),
      { apiPost, cache },
    );
  const results = await Promise.allSettled(
    list.map((p) =>
      fetchForecastReport(p.row_key, { apiGet, cache }).then((src) => {
        // `dist` carries THIS vintage's whole bucket distribution so the timeline can
        // draw a per-outcome history, not just the scalar headline. Genuine multi-value
        // only — a binary row yields null here (extractMultiway rejects a BooleanSpace),
        // and its two-slot YES/NO pair is synthesized at render time from `p`, where the
        // All Multiway flag is known. Same layering as `extractDistribution`. A fire with no
        // `triggers.contract` keeps its distribution only in `forecastValue`, so fall back to
        // it as `asyncEntrySnapshot` does.
        const dist = src
          ? extractMultiway({
              ...src,
              forecast_value: src.contract?.forecast_value ?? src.forecastValue,
            })
          : null;
        // A multi-value fire has no binary projection, so `pointerToMoment` drops it; keep
        // it with `p: null` so the per-outcome line still has this vintage. Scalar
        // consumers already skip a non-finite `p`.
        const moment = pointerToMoment(p, src) ?? distOnlyMoment(p, dist);
        return {
          moment: moment ? { ...moment, dist } : null,
          verdict: pointerToVerdict(p, src),
        };
      }),
    ),
  );
  const settled = results.filter((r) => r.status === "fulfilled" && r.value).map((r) => r.value);
  return {
    moments: settled.map((s) => s.moment).filter(Boolean),
    verdicts: settled.map((s) => s.verdict).filter(Boolean),
  };
}

/**
 * Returns an entry even when the fire carries NO verdict (`shift: null`), which lets the
 * aggregate tell "this contract has fires but none was judged" (render the empty state) from
 * "nothing loaded" (render nothing).
 *
 * `shift` carries the ruling plus the two filters (`gated`, `judge_error`) and the prior
 * probability, `display.kind` supplies the user-language reason for an unexplained move, and
 * `probability` is the other end of the move it reports.
 *
 * @param {object|null} pointer a kind:'forecast' report pointer
 * @param {object|null} source result of reportPayloadToSource (or null on miss)
 * @returns {{fireTs: string|null, shift: object|null, display: object|null,
 *   probability: number|null}|null}
 */
export function pointerToVerdict(pointer, source) {
  if (!pointer || !source) return null;
  return {
    fireTs: typeof pointer.fire_ts === "string" ? pointer.fire_ts : null,
    shift: source.shift ?? null,
    display: source.shiftDisplay ?? null,
    probability: typeof source.serverPrediction === "number" ? source.serverPrediction : null,
  };
}

// The server's /forecast/reports per-request key cap (_MAX_BATCH_REPORT_KEYS on
// the forecast server). Full-fidelity contracts can have thousands of fires, so
// the client chunks the misses into ≤this-many-key POSTs.
export const BATCH_REPORTS_CHUNK = 256;

/**
 * The response maps each requested key to a report payload or null. Each is cached as the
 * mapped source or a stable null, mirroring fetchForecastReport's 200/404 caching. A failed
 * or malformed batch leaves the misses UNCACHED so the per-key path refetches them; this
 * never throws.
 *
 * @param {string[]} rowKeys the forecast-pointer row keys to prime
 * @param {{ apiPost: Function, cache: object }} deps
 */
export async function primeForecastReportsCache(rowKeys, { apiPost, cache }) {
  const uniq = [
    ...new Set((Array.isArray(rowKeys) ? rowKeys : []).filter((k) => typeof k === "string" && k)),
  ];
  const misses = uniq.filter((k) => cache.get(k) === undefined);
  if (misses.length === 0) return;
  // Chunks run SEQUENTIALLY: each POST already fans out its keys concurrently server-side,
  // so firing all chunks at once would multiply the backend's concurrent BigTable reads.
  for (let i = 0; i < misses.length; i += BATCH_REPORTS_CHUNK) {
    const chunk = misses.slice(i, i + BATCH_REPORTS_CHUNK);
    let r;
    try {
      r = await apiPost("/api/forecast/reports", { row_keys: chunk });
    } catch {
      continue;
    }
    const reports =
      r && r.ok && r.body && typeof r.body.reports === "object" ? r.body.reports : null;
    if (!reports) continue;
    for (const k of chunk) {
      if (!Object.prototype.hasOwnProperty.call(reports, k)) continue;
      const payload = reports[k];
      cache.set(k, payload ? reportPayloadToSource(payload, k) : null);
    }
  }
}

// Preload bounds for `preloadForecastReportsFull`. The limit bounds the fan-out for
// contracts on a very frequent cadence; concurrency is capped so a preload can't starve the
// interactive per-scrub read sharing the same connection pool.
export const PRELOAD_REPORTS_LIMIT = 24;
export const PRELOAD_REPORTS_CONCURRENCY = 4;

/**
 * Background-warm the report cache with FULL (structured-report-bearing) sources,
 * via the single-key `/api/forecast/report` route.
 *
 * The batch `POST /forecast/reports` route omits the structured `report` block, so a
 * batch-primed entry can never satisfy a consumer that needs the report. Because
 * `fetchForecastReport` short-circuits on ANY cache hit, a lean entry prevents the
 * single-key read that would have returned it. The scrub surface needs the report, so it
 * warms through this instead; fetching ahead avoids both a cold read on each first scrub
 * and the structured grid flashing in after the narrative fallback already rendered.
 *
 * Best-effort and fire-and-forget-safe: never throws, only fetches cache misses, and leaves
 * a transiently-failed key UNCACHED so the interactive read retries it. The caller passes
 * keys in priority order (newest fire first); `limit` keeps the head.
 *
 * @param {string[]} rowKeys forecast-pointer row keys, highest priority first
 * @param {{ apiGet: Function, cache: object, limit?: number, concurrency?: number }} deps
 * @returns {Promise<void>}
 */
export async function preloadForecastReportsFull(
  rowKeys,
  { apiGet, cache, limit = PRELOAD_REPORTS_LIMIT, concurrency = PRELOAD_REPORTS_CONCURRENCY },
) {
  const uniq = [
    ...new Set((Array.isArray(rowKeys) ? rowKeys : []).filter((k) => typeof k === "string" && k)),
  ];
  // Clamp the cap to a non-negative integer: a negative `limit` would make the
  // slice below silently drop keys instead of capping (slice(0, -1) === all-but-one).
  const cap = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : PRELOAD_REPORTS_LIMIT;
  // Only misses, then cap — so an already-warm contract costs nothing and the cap
  // is spent on keys that actually need fetching.
  const misses = uniq.filter((k) => cache.get(k) === undefined).slice(0, cap);
  if (misses.length === 0) return;
  // At least 1 worker: a 0/negative/NaN `concurrency` must degrade to SERIAL, never to an
  // empty pool that resolves immediately having fetched nothing.
  const requested = Number.isFinite(concurrency)
    ? Math.floor(concurrency)
    : PRELOAD_REPORTS_CONCURRENCY;
  const workers = Math.max(1, Math.min(requested, misses.length));
  let next = 0;
  const worker = async () => {
    while (next < misses.length) {
      const rowKey = misses[next++];
      try {
        await fetchForecastReport(rowKey, { apiGet, cache });
      } catch {
        // Best-effort: a throwing apiGet leaves this key uncached (retryable) and
        // must not abort the rest of the preload.
      }
    }
  };
  await Promise.all(Array.from({ length: workers }, () => worker()));
}

/**
 * Project P(YES) from a `forecast_value`-shaped object, the fallback for a binary row whose
 * report carries no raw `prediction` cell. Mirrors the server-side projection
 * (`sooth_qgen.pool_codec.binary_scalar`): a `BooleanSpace`'s two-slot PMF stores
 * `mass[0] = P(NO)`, `mass[1] = P(YES)`, and this reads slot 1.
 *
 * Deliberately NOT `extractMultiway`: that helper returns null for a `BooleanSpace` on
 * purpose (a genuinely categorical two-outcome question stays multiway), and this is its
 * binary-only complement. The `form === "pmf"` discriminator is checked rather than just
 * whether `mass` exists, because the `Representation` union's other member, `Gaussian`,
 * carries no `mass` at all.
 *
 * @param {any} forecastValue a `forecast_value`-shaped object (or nullish)
 * @returns {number|null} P(YES), or null when not a valid binary PMF
 */
export function projectBinaryPrediction(forecastValue) {
  if (!forecastValue || typeof forecastValue !== "object") return null;
  const space = forecastValue.outcome_space;
  if (!space || typeof space !== "object" || space.kind !== "boolean") return null;
  const representation = forecastValue.representation;
  if (!representation || typeof representation !== "object" || representation.form !== "pmf") {
    return null;
  }
  const mass = representation.mass;
  if (!Array.isArray(mass) || mass.length !== 2) return null;
  const p = mass[1];
  return typeof p === "number" && Number.isFinite(p) ? p : null;
}

/**
 * A panel fire's contract, completed from the same fire. Venue (Kalshi /
 * Polymarket) fires store `resolution_date: ""`, and v4.1 multi-outcome fires store no
 * `forecast_value` / `outcome_labels` on the contract, only at the payload's top level. Left
 * as is, the overlay keeps the blank date (so `withLiveIdentity` never fills it: "Resolves —")
 * and drops the distribution (so the Brief reads a multi-outcome forecast as Yes/No). The
 * distribution filled here is the fire's own answer, never the live one.
 *
 * @param {object|null} contract the fire's `triggers.contract`
 * @param {any} forecastValue the payload's top-level `forecast_value`
 * @param {Record<string, number>|null|undefined} predictionPmf `triggers.prediction_pmf`
 * @returns {object|null} `contract` unchanged when there is nothing to complete
 */
export function completeFireContract(contract, forecastValue, predictionPmf) {
  if (!contract || typeof contract !== "object") return contract;
  const out = { ...contract };
  let changed = false;
  if (out.resolution_date === "") {
    delete out.resolution_date;
    changed = true;
  }
  // A placeholder, not criteria: let the live contract's (the report's reading) show instead.
  if (
    typeof out.resolution_criteria === "string" &&
    /^\s*not provided\.?\s*$/i.test(out.resolution_criteria)
  ) {
    delete out.resolution_criteria;
    changed = true;
  }
  const mass = forecastValue?.representation?.mass;
  const kind = forecastValue?.outcome_space?.kind;
  if (out.forecast_value == null && kind && kind !== "boolean" && Array.isArray(mass)) {
    out.forecast_value = forecastValue;
    changed = true;
    const keys =
      predictionPmf && typeof predictionPmf === "object" ? Object.keys(predictionPmf) : [];
    if (out.outcome_labels == null && keys.length === mass.length) out.outcome_labels = keys;
  }
  return changed ? out : contract;
}

/**
 * The structured executive `report` block is surfaced as both `report` and
 * `serverContext: {report}` (the shape the modal reads). A payload without `report`
 * (older/batch rows) maps both to null, so the overlay keeps the base source's block and
 * the modal falls back to the serverExplanation-parsed narrative. Returns null for an
 * empty/malformed payload so the caller falls back to the server_ctx render.
 *
 * `serverPrediction` is projected from `forecast_value` via `projectBinaryPrediction`, so it
 * is null for a multi-outcome or absent forecast.
 *
 * `rowKey` is the key the payload was read under; it is stamped onto the parsed
 * `alternateForecast` only (see extractAlternateForecast), so that field can name
 * the report it came from.
 */
export function reportPayloadToSource(payload, rowKey = null) {
  if (!payload || typeof payload !== "object") return null;
  const triggers = payload.triggers && typeof payload.triggers === "object" ? payload.triggers : {};
  // Defense-in-depth: the endpoint resolves a gs:// agentic-trace explanation to
  // rendered reasoning server-side (report_read._resolve_trace_reasoning), so a
  // raw gs:// pointer should never reach here. If an unresolved one ever does (an
  // older backend, or a resolution that was skipped), drop it — never render a
  // bucket URL as the forecast narrative; fall back to the base source instead.
  const explanation =
    typeof payload.explanation === "string" && !payload.explanation.startsWith("gs://")
      ? payload.explanation
      : null;
  // Structured executive block — network-sourced, so accept it ONLY as a PLAIN
  // object (the modal/export read `report.reasons_for` etc. as arrays; a non-plain
  // shape would propagate downstream). Backend JSON only yields plain objects, so
  // the prototype check is defense-in-depth (rejects a Date/Map/class instance a
  // non-JSON caller might hand in). Derive both `report` and its mirrored
  // `serverContext.report` from the SAME normalized value so they can't diverge
  // (e.g. a falsy-but-present payload leaving report set but serverContext null).
  // Not-a-plain-object / absent → both null → narrative fallback.
  const rawReport = payload.report;
  const reportProto =
    typeof rawReport === "object" && rawReport !== null ? Object.getPrototypeOf(rawReport) : false;
  const report = reportProto === Object.prototype || reportProto === null ? rawReport : null;
  // Shift-judge verdict on THIS fire's move vs the prior fire. Panel-cron AND
  // on-demand rows carry it — absent on a first fire on that row's own verdict chain
  // (nothing to compare; the panel and on-demand chains are separate, so a long panel
  // history gives a first on-demand fire no prior), on a lane the judge is off for,
  // and on panel_meta. Its absence is MEANINGFUL: we build the nested
  // `shift` object only when a verdict is actually present, so a missing verdict
  // yields `shift: null` and the aggregate renders nothing — never a fabricated
  // "unjustified" (a false `shift_justified` is indistinguishable from a ruled one,
  // so `justified` preserves the true/false/null distinction). This group drives the
  // FORECAST STABILITY aggregate only; see the shift_* contract doc in
  // src/types/forecastServer.ts.
  //
  // `shift_summary` is DELIBERATELY NOT MAPPED. It is the judge's own internal one-liner
  // ("own verdict (noise): …", "followed <lane> 0.51->0.75, itself ruled noise") — harness
  // internals in the harness's voice, naming lanes that do not exist as far as the user is
  // concerned. The user-facing prose comes from `shift_display_text` below, which the backend
  // writes in the auditor's voice. Not mapping it is the point: nothing downstream can render
  // it by accident.
  const hasShift =
    typeof payload.shift_justified === "boolean" || typeof payload.shift_reason_class === "string";
  const shift = hasShift
    ? {
        justified: typeof payload.shift_justified === "boolean" ? payload.shift_justified : null,
        reasonClass:
          typeof payload.shift_reason_class === "string" ? payload.shift_reason_class : null,
        gated: payload.shift_gated === true,
        prevProbability:
          typeof payload.shift_prev_probability === "number"
            ? payload.shift_prev_probability
            : null,
      }
    : null;
  // The DISPLAY block — the only shift data that is rendered as prose. The backend judge writes
  // it already user-ready: one of three kinds (evidence / interpretation / reevaluation) plus a
  // ≤240-char sentence in the auditor's voice with no internals in it. `text` is rendered
  // VERBATIM — no post-processing, no re-wording, no appending — because any client-side
  // rewrite would be this UI editorializing over a judged finding.
  //
  // Built only when the kind is one this UI knows: an absent/unrecognized kind means the fire
  // has no displayable verdict (no_shift, judge_error, or no verdict at all) and the box must
  // render NOTHING rather than guess. The two `read_*` fields exist ONLY for `interpretation`
  // (the old and new reading of the resolution criteria) and are dropped for the other kinds so
  // a stray value can't render under the wrong heading.
  const kind = payload.shift_display_kind;
  const str = (v) => (typeof v === "string" && v.length > 0 ? v : null);
  const shiftDisplay = SHIFT_DISPLAY_KINDS.includes(kind)
    ? {
        kind,
        text: str(payload.shift_display_text),
        readBefore: kind === "interpretation" ? str(payload.shift_display_read_before) : null,
        readAfter: kind === "interpretation" ? str(payload.shift_display_read_after) : null,
      }
    : null;
  return {
    // The wire's `contract.context` is the record's `background`.
    contract: withBackground(
      completeFireContract(
        triggers.contract ?? null,
        payload.forecast_value,
        triggers.prediction_pmf,
      ),
    ),
    events: Array.isArray(triggers.events) ? triggers.events : null,
    // Distinguishes a FAILED trigger-gen run (postprocess errored, so `triggers` is
    // empty for a reason that has nothing to do with the timeline) from a genuinely
    // empty trigger list. The backend surfaces it precisely so a caller can tell the
    // two apart (report_read.build_forecast_report_payload); rendering a failed run as
    // "this vintage had no triggers" is a lie about the forecast. Absent → null.
    postprocessStatus:
      typeof payload.postprocess_status === "string" ? payload.postprocess_status : null,
    // Derived here so an overlaid source keeps the answer rather than re-deriving it from
    // fields `applyPointerOverrides` does not carry. See `unauthoredRun.js` for why the
    // conjunction is the test and why `prediction` is not part of it.
    unauthored: runAuthoredNoForecast({ postprocess_status: payload.postprocess_status, report }),
    serverExplanation: explanation,
    serverPrediction: projectBinaryPrediction(payload.forecast_value),
    // The vintage's full distribution, RAW. `serverPrediction` above is only its binary
    // projection (null for a multi-value space), and a scheduled (panel) fire's
    // `triggers.contract` is EMPTY, so past marks on a multi-value forecast need this.
    forecastValue: payload.forecast_value ?? null,
    report,
    serverContext: report ? { report } : null,
    shift,
    shiftDisplay,
    // Only the single-key GET /forecast/report carries this, and only when the SERVER-side
    // `sooth-forecast-alternate-spaces` flag is on. Rendering is gated separately,
    // client-side, on `chatboard-alternate-spaces`.
    alternateForecast: extractAlternateForecast(payload, rowKey),
  };
}

/**
 * An override field only wins when non-null, so a partial read never clobbers a populated
 * base field. Returns the base object unchanged (same reference) when there is nothing to
 * overlay.
 *
 * @param {object|null} base the existing report render source (the report wiring's
 *   `reportSnapshot` state)
 * @param {object|null} pointerSource result of reportPayloadToSource (or null)
 * @param {object|null} resolutionPayload a fetched /resolve/report payload (or null)
 */
export function applyPointerOverrides(base, pointerSource, resolutionPayload) {
  if (!pointerSource && !resolutionPayload) return base;
  const out = { ...(base || {}) };
  if (pointerSource) {
    // The fire's contract replaces the base one, keeping the base's question identity —
    // a pre-parity fire has none, and every fire re-words it (see withLiveIdentity).
    if (pointerSource.contract != null)
      out.contract = withLiveIdentity(pointerSource.contract, base?.contract);
    if (pointerSource.events != null) out.events = pointerSource.events;
    if (pointerSource.serverExplanation != null)
      out.serverExplanation = pointerSource.serverExplanation;
    if (pointerSource.serverPrediction != null)
      out.serverPrediction = pointerSource.serverPrediction;
    // The shift verdict lives only on the pointer-read report row, never on the base
    // server_ctx source.
    if (pointerSource.shift != null) out.shift = pointerSource.shift;
    if (pointerSource.shiftDisplay != null) out.shiftDisplay = pointerSource.shiftDisplay;
    // The single-key GET /forecast/report carries the structured report (include_report=True)
    // for BOTH report-tool lanes (bridged from a report-shaped `explanation`) and rows with
    // a dedicated `content:report` cell, so a forecast whose report exists only in BigTable
    // can still render the structured grid instead of the legacy parsed narrative.
    // `serverContext` is MERGED, not replaced: the base's other server_ctx keys (e.g.
    // `explanation`) are read elsewhere and must survive.
    if (pointerSource.report != null) {
      out.report = pointerSource.report;
      out.serverContext = { ...(out.serverContext || {}), report: pointerSource.report };
    }
    // The flag propagates whenever non-null (not on truthiness), so a fresh read can clear a
    // stale true.
    if (pointerSource.unauthored != null) out.unauthored = pointerSource.unauthored;
    // These describe the selected row only, so a present property wins even when null — a
    // row with no alternate must clear the base's.
    if ("alternateForecast" in pointerSource)
      out.alternateForecast = pointerSource.alternateForecast;
  }
  // A fetched resolution is preferred over the base's server_ctx copy, being the freshest
  // settled attempt for the qkey; an absent read leaves the base resolution. The
  // /api/resolve/report payload is FLAT ({status, resolution_date, resolved_at, confidence,
  // reasoning, key_evidence, open_reasons, missing_evidence}), but the modal reads resolution
  // detail from `resolution.report.*`, so those report fields are normalized into it.
  if (resolutionPayload) {
    const prevRes = out.resolution || {};
    // The read now returns the best SETTLED attempt — a conclusive verdict when one
    // exists, else an `unresolved` one. That fallback must not be allowed to
    // DOWNGRADE a conclusive verdict the base already holds: a contract resolved via
    // the venue/notifier path carries a conclusive server_ctx.resolution even when
    // its qkey has no conclusive resolver row, and overlaying an unresolved attempt
    // there would flip a decided question to UNRESOLVED in the UI. Skip the overlay
    // wholesale in that case rather than merging field-by-field — taking the
    // unresolved body while keeping the conclusive status would render a verdict
    // alongside "why I couldn't decide", which is worse than either.
    //
    // A settled MULTI-VALUE verdict is conclusive too, though it carries no binary token:
    // the notifier stores it as a named `resolved_outcome`. The read never returns one —
    // /resolve/report is scoped to the binary set by design — so for a resolved
    // categorical question it hands back the last `unresolved` attempt, and overlaying
    // that relabelled the decided question UNRESOLVED with the stale diagnostics.
    if (
      (isConclusiveResolutionStatus(prevRes.status) || isNamedOutcomeVerdict(prevRes)) &&
      !isConclusiveResolutionStatus(resolutionPayload.status)
    ) {
      return out;
    }
    const merged = { ...prevRes, report: { ...(prevRes.report || {}) } };
    if (resolutionPayload.status != null) merged.status = resolutionPayload.status;
    if (resolutionPayload.resolution_date != null)
      merged.resolution_date = resolutionPayload.resolution_date;
    if (resolutionPayload.resolved_at != null) merged.resolved_at = resolutionPayload.resolved_at;
    if (resolutionPayload.confidence != null)
      merged.report.confidence = resolutionPayload.confidence;
    if (resolutionPayload.reasoning != null) merged.report.reasoning = resolutionPayload.reasoning;
    if (resolutionPayload.key_evidence != null)
      merged.report.key_evidence = resolutionPayload.key_evidence;
    // The modal renders open_reasons and missing_evidence only when status is "unresolved".
    if (resolutionPayload.open_reasons != null)
      merged.report.open_reasons = resolutionPayload.open_reasons;
    if (resolutionPayload.missing_evidence != null)
      merged.report.missing_evidence = resolutionPayload.missing_evidence;
    out.resolution = merged;
  }
  return out;
}

/**
 * The STABLE "missing row" negative (404) is cached as null so a fallback-to-server_ctx
 * render doesn't re-fetch every render within the TTL. A TRANSIENT failure (network / 5xx)
 * is NOT cached, so it retries once the upstream recovers.
 *
 * @param {string|null} rowKey the forecast pointer's `row_key`
 * @param {{ apiGet: Function, cache: object }} deps injected apiGet + TTL cache
 */
export async function fetchForecastReport(rowKey, { apiGet, cache }) {
  if (!rowKey) return null;
  const cached = cache.get(rowKey);
  if (cached !== undefined) return cached;
  const r = await apiGet(`/api/forecast/report?row_key=${encodeURIComponent(rowKey)}`);
  if (r && r.ok && r.body) {
    const src = reportPayloadToSource(r.body, rowKey);
    cache.set(rowKey, src);
    return src;
  }
  if (r && r.status === 404) cache.set(rowKey, null);
  return null;
}

// Reads still out, per cache and row (see fetchForecastReportJoined).
const _forecastReadsInFlight = new WeakMap();

/**
 * fetchForecastReport, except that a caller for a row whose read is still out joins that read
 * rather than starting a second — the report window opening while the early read made on
 * selection is in flight. A stored report row is immutable for the life of the run, so the
 * joined answer is the one a second read would give.
 *
 * @param {string|null} rowKey the forecast pointer's `row_key`
 * @param {{ apiGet: Function, cache: object }} deps injected apiGet + TTL cache
 * @returns {Promise<object|null>}
 */
export function fetchForecastReportJoined(rowKey, { apiGet, cache }) {
  if (!rowKey) return Promise.resolve(null);
  let reads = _forecastReadsInFlight.get(cache);
  if (!reads) _forecastReadsInFlight.set(cache, (reads = new Map()));
  let read = reads.get(rowKey);
  if (!read) {
    read = fetchForecastReport(rowKey, { apiGet, cache }).finally(() => reads.delete(rowKey));
    reads.set(rowKey, read);
  }
  return read;
}

/**
 * The STABLE "no conclusive attempt yet" negative (204) is cached as null. A TRANSIENT
 * failure (network / 5xx) is NOT cached, so it retries once `/api/resolve/report` recovers
 * rather than suppressing the verdict for the TTL.
 *
 * @param {string|null} qkey forecasts.contractKey (== the resolver qkey)
 * @param {{ apiGet: Function, cache: object }} deps injected apiGet + TTL cache
 */
export async function fetchResolutionReport(qkey, { apiGet, cache }) {
  if (!qkey) return null;
  const cached = cache.get(qkey);
  if (cached !== undefined) return cached;
  const r = await apiGet(`/api/resolve/report?qkey=${encodeURIComponent(qkey)}`);
  if (r && r.ok && r.body && r.body.status) {
    cache.set(qkey, r.body);
    return r.body;
  }
  if (r && r.status === 204) cache.set(qkey, null);
  return null;
}

/**
 * The `!ok` answers of `/api/resolve/report` a prefetch expects, so a warm-up they end is not
 * a lost one to count (`resolution_prefetch.failed`): a 503 whose body says
 * `resolver_unavailable` (no resolver on this plane), a 403 `qkey_forbidden` (the viewer may
 * not read this contract) and a 504 (the resolver timed out; the modal's own read retries).
 * Every other `!ok` counts — a 404 included, since the report path cannot produce one, and a
 * 503 or 403 without its reason word. The 204 "no conclusive report yet" is a 2xx and never
 * reaches this test.
 * @param {number|undefined} status
 * @param {string|null|undefined} reason the body's `status` (503) or `error` (403) word
 * @returns {boolean}
 */
export function isExpectedResolutionMiss(status, reason) {
  if (status === 504) return true;
  if (status === 503) return reason === "resolver_unavailable";
  if (status === 403) return reason === "qkey_forbidden";
  return false;
}

/**
 * The reason word a `!ok` resolver answer carries: `{status:"resolver_unavailable"}` on a 503,
 * `{error:"qkey_forbidden"}` on a 403; null for any other body.
 * @param {unknown} body
 * @returns {string|null}
 */
export function resolutionMissReason(body) {
  if (!body || typeof body !== "object") return null;
  const word = /** @type {{status?: unknown, error?: unknown}} */ (body).status;
  if (typeof word === "string") return word;
  const error = /** @type {{error?: unknown}} */ (body).error;
  return typeof error === "string" ? error : null;
}
